import { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'antd';
import { Container } from '@/components/Elements';
import { InputField, RadioGroupField } from '@/components/Form';
import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import { OPTIONAL_AGREE_OPTIONS, STATES_OPTIONS } from '@/constant/options';
import { propertyDetailLabelMapper } from '../schema/propertyDetailSchema';
import useScrollToTop from '@/hooks/useScrollTop';
import { useGetPersonalPropertyListQuery } from '@/pages/mortgage/apis/propertyAPI';
import { PlusOutlined } from '@ant-design/icons';
import PropertyFormModal from '@/pages/property/components/PropertyFormModal';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import PropertyItems from '@/pages/mortgage/components/Property/PropertyItems';

interface Props {
  data: any;
  setValue: any;
  watch: any;
  control: any;
  errors: any;
  unregister: any;
  resetField: any;
}

const NewPropertyDetail: React.FC<Props> = ({
  data: propertyDetailData,
  setValue,
  watch,
  control,
  errors,
  unregister,
  resetField,
}) => {
  useUnsavedChangesWarning(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useScrollToTop();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [overseasModal, setOverseasModal] = useState(false);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  console.log('errors', errors);

  const [selectedProperties, setSelectedProperties] = useState<
    (string | number)[]
  >([]);

  const [overseasSelectedProperties, setOverseasSelectedProperties] = useState<
    (string | number)[]
  >([]);

  useEffect(() => {
    if (propertyDetailData) {
      const props = propertyDetailData?.propertyIds || [];
      const overseasProps = propertyDetailData?.overseasPropertyIds || [];

      setSelectedProperties(props);
      setValue('propertyIds', props);

      setOverseasSelectedProperties(overseasProps);
      setValue('overseasPropertyIds', overseasProps);

      setValue('maxPurchaseAmount', propertyDetailData?.maxPurchaseAmount);
      setValue('propertyLocation', propertyDetailData?.propertyLocation);
      setValue(
        'maxDepositDownpayment',
        propertyDetailData?.maxDepositDownpayment,
      );
      setValue(
        'useExistingAusProperty',
        propertyDetailData?.useExistingAusProperty,
      );
    }
  }, [propertyDetailData, setValue]);

  const { data: propertyData, refetch: refetchPropertyData } =
    useGetPersonalPropertyListQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const [australianPropertyData, setAustralianPropertyData] = useState<any[]>(
    [],
  );
  const [overseasPropertyData, setOverseasPropertyData] = useState<any[]>([]);

  useEffect(() => {
    if (propertyData?.data) {
      const australianProperties = propertyData.data.filter(
        (property: any) => property.propertyLocation === 'australian',
      );
      const overseasProperties = propertyData.data.filter(
        (property: any) => property.propertyLocation === 'overseas',
      );

      setAustralianPropertyData(australianProperties);
      setOverseasPropertyData(overseasProperties);
    }
  }, [propertyData]);

  const handlePropertySelect = (propertyId: number) => {
    setSelectedProperties((prevSelected) => {
      const updatedSelection = prevSelected.includes(propertyId)
        ? prevSelected.filter((id) => id !== propertyId)
        : [...prevSelected, propertyId];

      setValue('propertyIds', updatedSelection);
      return updatedSelection;
    });
  };

  const handleOverseasPropertySelect = (propertyId: number) => {
    setOverseasSelectedProperties((prevSelected) => {
      const updatedSelection = prevSelected.includes(propertyId)
        ? prevSelected.filter((id) => id !== propertyId)
        : [...prevSelected, propertyId];

      setValue('overseasPropertyIds', updatedSelection);
      return updatedSelection;
    });
  };

  const handleNewPropertyAdded = (newProperty: any) => {
    if (overseasModal) {
      setOverseasSelectedProperties((prevSelected) => {
        const updatedSelection = [...prevSelected, newProperty.id];
        setValue('overseasPropertyIds', updatedSelection);
        return updatedSelection;
      });
    } else {
      setSelectedProperties((prevSelected) => {
        const updatedSelection = [...prevSelected, newProperty.id];
        setValue('propertyIds', updatedSelection);
        return updatedSelection;
      });
    }
  };

  useEffect(() => {
    if (selectedProperties.length === 0) {
      resetField('useExistingAusProperty');
    }
  }, [selectedProperties, resetField]);

  return (
    <Container width="md">
      <Row>
        <Col xs={24}>
          <Row gutter={30}>
            <Col xs={24}>
              <div className="form-section-header form-control">
                <FormMainLabel
                  index={8}
                  label={propertyDetailLabelMapper['maxPurchaseAmount']}
                />

                <InputField
                  name="maxPurchaseAmount"
                  control={control}
                  error={errors.maxPurchaseAmount?.message ?? ''}
                  placeholder="Enter your amount in AUD $"
                  size="large"
                  type="number"
                  prefix="AUD"
                />
              </div>
            </Col>

            <Col xs={24}>
              <div
                className="form-section-header form-control"
                id="propertyLocation"
              >
                <FormMainLabel
                  index={9}
                  label={propertyDetailLabelMapper['propertyLocation']}
                />
                <RadioGroupField
                  name="propertyLocation"
                  control={control}
                  options={STATES_OPTIONS}
                  error={errors.propertyLocation?.message}
                  cols={{ sm: 1, md: 1, lg: 2 }}
                  size="large"
                />
              </div>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col xs={24}>
              <div className="form-section-header form-control">
                <FormMainLabel
                  index={10}
                  label={propertyDetailLabelMapper['maxDepositDownpayment']}
                />
                <InputField
                  name="maxDepositDownpayment"
                  control={control}
                  error={errors.maxDepositDownpayment?.message ?? ''}
                  placeholder={`Enter your amount in ${
                    overseasModal ? 'local currency' : 'AUD'
                  }`}
                  size="large"
                  type="number"
                  prefix={overseasModal ? 'Local currency' : 'AUD'}
                />
              </div>
            </Col>

            <Col xs={24}>
              <div
                className="form-section-header form-control"
                id="ausProperty"
              >
                <FormMainLabel
                  index={11}
                  label={propertyDetailLabelMapper['ausProperty']}
                />
              </div>
            </Col>
            <Col xs={24}>
              {australianPropertyData?.map((property: any, index: number) => {
                return (
                  <div key={index}>
                    <PropertyItems
                      key={property.id}
                      property={property}
                      index={index}
                      selectedProperties={selectedProperties}
                      onSelect={handlePropertySelect}
                      refetchPropertyData={refetchPropertyData}
                      overseas={false}
                    />
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Button
                  size="large"
                  onClick={() => {
                    setOverseasModal(false);
                    setIsModalOpen(true);
                  }}
                >
                  <PlusOutlined />
                  Add Property
                </Button>
              </div>
            </Col>
          </Row>
          {selectedProperties.length > 0 && (
            <Row gutter={30} id="useExistingAusProperty">
              <Col xs={24}>
                <div className="form-section-header form-control">
                  <FormMainLabel
                    index={12}
                    label={propertyDetailLabelMapper['useExistingAusProperty']}
                  />
                  <RadioGroupField
                    name="useExistingAusProperty"
                    control={control}
                    options={OPTIONAL_AGREE_OPTIONS}
                    error={errors.useExistingAusProperty?.message}
                    cols={{ sm: 1, md: 1, lg: 3 }}
                    size="large"
                  />
                </div>
              </Col>
            </Row>
          )}
          <Row gutter={30} id="overseasProperty">
            <Col xs={24}>
              <div className="form-section-header form-control">
                <FormMainLabel
                  index={selectedProperties.length > 0 ? 13 : 12}
                  label={propertyDetailLabelMapper['overseasProperty']}
                />
              </div>
            </Col>
            <Col xs={24}>
              {overseasPropertyData?.map((property: any, index: number) => {
                return (
                  <div key={index}>
                    <PropertyItems
                      key={property.id}
                      property={property}
                      index={index}
                      selectedProperties={overseasSelectedProperties}
                      onSelect={handleOverseasPropertySelect}
                      refetchPropertyData={refetchPropertyData}
                      overseas={true}
                    />
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Button
                  size="large"
                  onClick={() => {
                    setOverseasModal(true);
                    setIsModalOpen(true);
                  }}
                >
                  <PlusOutlined />
                  Add Property
                </Button>
              </div>
            </Col>
          </Row>
          <Col xs={24}>
            <Button
              ref={submitButtonRef}
              type="primary"
              htmlType="submit"
              style={{ display: 'none' }}
            >
              Submit
            </Button>
          </Col>
        </Col>
      </Row>
      <PropertyFormModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        overseas={overseasModal}
        propertyOption="personal"
        onNewPropertyAdded={handleNewPropertyAdded}
      />
    </Container>
  );
};

export default NewPropertyDetail;
